import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';

import {
  accordionScopeFor,
  accordionTriggerFor,
  anchorTargetsSidebar,
  closeSidebarDrawer,
  collapseAccordionsExcept,
  ensureSidebarUsable,
  isVisible,
  keepAnchorInView,
  openAccordion,
  resolveAnchor,
  restoreSidebarShape,
  sidebarShape,
  waitForDom,
  type AnchorStrategy,
  type SidebarRestore,
  type SidebarShape,
} from './anchors';
import { restoreHostChrome, suppressHostChrome } from './hostChrome';
import {
  dressGuide,
  groupFits,
  guideHeight,
  guideMode,
  keepTrackerClear,
  liveGuide,
  makeRoomFor,
  releaseRoom,
  visibleBand,
} from './room';
import type { Tour, TourStep } from '../tours';

export interface TourRunState {
  running: boolean;
  tourId: string | null;
  stepId: string | null;
  index: number;
  total: number;
  order: string[];
}

export interface StartResult {
  started: boolean;
  resolved: { stepId: string; strategy: AnchorStrategy }[];
  skipped: string[];
}

const DRIVER_ACTIVE_CLASS = 'driver-active';

const SETTLE_MS = 520;

const SETTLE_STILL_FRAMES = 2;

const SUBMENU_MOUNT_TIMEOUT_MS = 600;

const HOST_SETTLE_TIMEOUT_MS = 2500;

const HOST_SETTLE_STILL_MS = 300;

const DRIVER_ARIA_ATTRS = ['aria-expanded', 'aria-controls', 'aria-haspopup'] as const;

const TASK_CLASS = 'tour-guide-task';

const SKIP_CLASS = 'tour-guide-skip';

const SKIP_TOUR_CLASS = 'tour-guide-skip-tour';

export interface TourResumeToken {
  tourId: string;
  stepId: string;
  expanded: string[];
  moduleCount: number;
}

export interface TourEngineOptions {
  onState(state: TourRunState): void;
  onComplete(tourId: string): void;
  accent?: string;
  taskOffer?(stepId: string): { label: string } | null;
  onTaskStart?(stepId: string): void;
}

interface RunEntry {
  step: TourStep;
  el: HTMLElement;
  strategy: AnchorStrategy;
  scopeLabels?: string[];
}

export class TourEngine {
  private instance: Driver | null = null;
  private options: TourEngineOptions;

  private furthestIndex = -1;

  private suppressed: HTMLElement[] = [];

  private runOrder: string[] = [];

  private tourId: string | null = null;

  private run: RunEntry[] = [];

  private driveSteps: DriveStep[] = [];

  private childrenByParent = new Map<string, TourStep[]>();

  private expandableCounts = new Map<string, number>();

  private expanded = new Set<string>();

  private expandChain: Promise<void> = Promise.resolve();

  private hostAria = new Map<HTMLElement, (string | null)[]>();

  private sidebarRestore: SidebarRestore | null = null;

  private shape: SidebarShape = 'absent';

  private settleFrame: number | null = null;

  private stopTracking: (() => void) | null = null;

  private stopEscape: (() => void) | null = null;

  constructor(options: TourEngineOptions) {
    this.options = options;
  }

  static isAnyTourActive(): boolean {
    return (
      document.body.classList.contains(DRIVER_ACTIVE_CLASS) ||
      document.documentElement.classList.contains(DRIVER_ACTIVE_CLASS)
    );
  }

  async open(tour: Tour): Promise<StartResult> {
    if (TourEngine.isAnyTourActive()) {
      return { started: false, resolved: [], skipped: [] };
    }
    this.sidebarRestore ??= await ensureSidebarUsable();
    const result = this.start(tour);
    if (!result.started) {
      restoreSidebarShape(this.sidebarRestore);
      this.sidebarRestore = null;
    }
    return result;
  }

  start(tour: Tour): StartResult {
    if (TourEngine.isAnyTourActive()) {
      return { started: false, resolved: [], skipped: [] };
    }

    const childrenByParent = new Map<string, TourStep[]>();
    const topLevel: TourStep[] = [];
    for (const step of tour.steps) {
      if (step.parentStepId === undefined) {
        topLevel.push(step);
        continue;
      }
      const siblings = childrenByParent.get(step.parentStepId);
      if (siblings) siblings.push(step);
      else childrenByParent.set(step.parentStepId, [step]);
    }

    const resolved: RunEntry[] = [];
    const skipped: string[] = [];

    for (const step of topLevel) {
      const hit = resolveAnchor(step.anchor);
      if (hit) resolved.push({ step, el: hit.element, strategy: hit.strategy });
      else skipped.push(step.id);
    }

    if (tour.order === 'dom') {
      resolved.sort((a, b) => {
        const groupDelta = (a.step.group ?? 0) - (b.step.group ?? 0);
        if (groupDelta !== 0) return groupDelta;
        if (a.el === b.el) return 0;
        const rel = a.el.compareDocumentPosition(b.el);
        if (rel & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
        if (rel & Node.DOCUMENT_POSITION_PRECEDING) return 1;
        return 0;
      });
    }

    if (resolved.length === 0) {
      console.warn(`[tour-guide] "${tour.label}": no targets on screen — nothing to show.`);
      return { started: false, resolved: [], skipped };
    }

    this.furthestIndex = -1;
    this.suppressed = suppressHostChrome();
    this.tourId = tour.id;
    this.run = resolved;
    this.childrenByParent = childrenByParent;
    this.driveSteps = resolved.map((entry) => this.driveStep(entry));
    this.runOrder = resolved.map((r) => r.step.id);
    this.expanded = new Set();
    this.expandChain = Promise.resolve();

    this.recountExpandable();
    this.shape = sidebarShape();

    const instance = driver({
      steps: this.driveSteps,
      animate: false,
      showProgress: true,
      allowClose: false,
      overlayColor: 'rgba(15, 23, 42, 0.68)',
      stagePadding: 6,
      stageRadius: 8,
      popoverClass: 'tour-guide-popover',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Done',
      onNextClick: () => {
        void this.afterPendingExpand(instance, () => {
          void this.navigateTo((instance.getActiveIndex() ?? 0) + 1);
        });
      },
      onPrevClick: () => {
        void this.afterPendingExpand(instance, () => {
          const at = instance.getActiveIndex() ?? 0;
          if (at > 0) void this.navigateTo(at - 1);
        });
      },
      onPopoverRender: (popover) => {
        const at = instance.getActiveIndex() ?? -1;
        const entry = this.run[at];
        if (!entry) return;
        const task = this.buildTaskButton(entry.step);
        if (task) popover.wrapper.insertBefore(task, popover.footer);
        const skip = this.buildSkipModuleButton(at);
        if (skip) popover.wrapper.insertBefore(skip, popover.footer);
        const leave = this.buildSkipTourButton(at);
        if (leave) popover.title?.append(leave);
        this.keepModalsOpen(popover.wrapper);
        this.fitStep(at, entry, popover.wrapper);
      },
      onHighlightStarted: (element) => {
        this.dropStaleActiveMarks(element);
        this.rememberHostAria(element);
        const at = instance.getActiveIndex?.() ?? 0;
        this.furthestIndex = Math.max(this.furthestIndex, at);
        const stepId = this.run[at]?.step.id ?? null;

        if (stepId && this.expandableCounts.has(stepId) && !this.expanded.has(stepId)) {
          void this.expandChildren(stepId);
        }

        this.options.onState({
          running: true,
          tourId: tour.id,
          stepId,
          index: at,
          total: this.run.length,
          order: this.runOrder,
        });
      },
      onDestroyed: () => {
        const reachedEnd = this.furthestIndex >= this.run.length - 1;
        this.stopTracking?.();
        this.stopTracking = null;
        this.stopEscape?.();
        this.stopEscape = null;
        if (this.settleFrame !== null) window.cancelAnimationFrame(this.settleFrame);
        this.settleFrame = null;
        restoreHostChrome(this.suppressed);
        this.suppressed = [];
        this.restoreHostAria();
        releaseRoom();
        restoreSidebarShape(this.sidebarRestore);
        this.sidebarRestore = null;
        this.instance = null;
        const total = this.run.length;
        this.tourId = null;
        this.runOrder = [];
        this.run = [];
        this.driveSteps = [];
        this.childrenByParent = new Map();
        this.expandableCounts = new Map();
        this.expanded = new Set();
        this.expandChain = Promise.resolve();
        this.shape = 'absent';
        this.options.onState({
          running: false,
          tourId: null,
          stepId: null,
          index: -1,
          total,
          order: [],
        });
        if (reachedEnd) this.options.onComplete(tour.id);
      },
    });

    this.instance = instance;
    this.bindEscapeToStop();
    instance.drive();
    this.startTracking(instance);

    const first = this.run[0];
    if (first) {
      void this.prepareGroups(first).then(() => {
        if (this.instance === instance) this.settleLayout(instance);
      });
    }

    return {
      started: true,
      resolved: resolved.map((r) => ({ stepId: r.step.id, strategy: r.strategy })),
      skipped,
    };
  }

  goToStep(stepId: string): boolean {
    if (!this.instance) return false;
    const index = this.runOrder.indexOf(stepId);
    if (index < 0) return false;
    void this.navigateTo(index);
    return true;
  }

  async goTo(stepId: string): Promise<boolean> {
    if (!this.instance) return false;
    const index = this.runOrder.indexOf(stepId);
    if (index < 0) return false;
    await this.navigateTo(index);
    return true;
  }

  next(): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      void this.navigateTo((instance.getActiveIndex() ?? 0) + 1);
    });
    return true;
  }

  previous(): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      const at = instance.getActiveIndex() ?? 0;
      if (at > 0) void this.navigateTo(at - 1);
    });
    return true;
  }

  nextModule(fromStepId?: string): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      const at = this.indexOfStop(instance, fromStepId);
      void this.navigateTo(this.nextModuleIndex(this.moduleIndexFor(at)));
    });
    return true;
  }

  previousFrom(fromStepId?: string): boolean {
    const instance = this.instance;
    if (!instance) return false;
    void this.afterPendingExpand(instance, () => {
      const at = this.indexOfStop(instance, fromStepId);
      if (at > 0) void this.navigateTo(at - 1);
    });
    return true;
  }

  private indexOfStop(instance: Driver, stepId?: string): number {
    const named = stepId ? this.runOrder.indexOf(stepId) : -1;
    return named >= 0 ? named : instance.getActiveIndex() ?? 0;
  }

  private moduleIndexFor(at: number): number {
    for (let i = Math.min(at, this.run.length - 1); i >= 0; i--) {
      if (this.run[i].step.parentStepId === undefined) return i;
    }
    return at;
  }

  private nextModuleIndex(from: number): number {
    for (let i = from + 1; i < this.run.length; i++) {
      if (this.run[i].step.parentStepId === undefined) return i;
    }
    return this.run.length;
  }

  private bindEscapeToStop(): void {
    this.stopEscape?.();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (!this.instance) return;
      this.stop();
    };

    document.addEventListener('keydown', onKeyDown, true);
    this.stopEscape = () => document.removeEventListener('keydown', onKeyDown, true);
  }

  stop(): void {
    this.instance?.destroy();
    this.instance = null;
  }

  pauseForTask(preferredStepId?: string): TourResumeToken | null {
    const instance = this.instance;
    if (!instance || !this.tourId) return null;

    const at = instance.getActiveIndex() ?? -1;
    const requested =
      preferredStepId && this.runOrder.includes(preferredStepId) ? preferredStepId : null;
    const stepId = requested ?? this.run[at]?.step.id ?? null;
    const token: TourResumeToken | null = stepId
      ? {
          tourId: this.tourId,
          stepId,
          expanded: [...this.expanded],
          moduleCount: this.run.filter((r) => r.step.parentStepId === undefined).length,
        }
      : null;

    this.stop();
    return token;
  }

  async resumeFrom(token: TourResumeToken, tour: Tour): Promise<boolean> {
    if (TourEngine.isAnyTourActive()) return false;

    await this.waitForHostToSettle(tour, token.moduleCount);

    if (!(await this.open(tour)).started) return false;

    for (const parentStepId of token.expanded) {
      await this.expandChildren(parentStepId);
    }

    return this.goTo(token.stepId);
  }

  private async waitForHostToSettle(tour: Tour, want: number): Promise<void> {
    const topLevel = tour.steps.filter((s) => s.parentStepId === undefined);
    if (topLevel.length === 0) return;

    const resolvable = () => topLevel.reduce((n, s) => (resolveAnchor(s.anchor) ? n + 1 : n), 0);
    const nextFrame = () =>
      new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));

    const deadline = Date.now() + HOST_SETTLE_TIMEOUT_MS;
    let previous = -1;
    let stillSince = Date.now();

    while (Date.now() < deadline) {
      const count = resolvable();
      if (want > 0 && count >= want) return;

      if (count !== previous) {
        previous = count;
        stillSince = Date.now();
      } else if (
        count > 0 &&
        Date.now() - stillSince >= HOST_SETTLE_STILL_MS
      ) {
        return;
      }

      await nextFrame();
    }
  }

  destroy(): void {
    this.stop();
  }

  private rememberHostAria(element: Element | undefined): void {
    if (!(element instanceof HTMLElement) || this.hostAria.has(element)) return;
    this.hostAria.set(
      element,
      DRIVER_ARIA_ATTRS.map((attr) => element.getAttribute(attr)),
    );
  }

  private restoreHostAria(): void {
    this.hostAria.forEach((values, element) => {
      DRIVER_ARIA_ATTRS.forEach((attr, i) => {
        const original = values[i];
        if (original === null) element.removeAttribute(attr);
        else element.setAttribute(attr, original);
      });
    });
    this.hostAria = new Map();
  }

  private startTracking(instance: Driver): void {
    let frame: number | null = null;
    let resized = false;

    const sync = () => {
      frame = null;
      if (this.instance !== instance) return;
      if (resized) {
        resized = false;
        void this.adaptToViewport(instance);
        return;
      }
      if (this.syncActiveAnchor()) this.readopt(instance);
      else instance.refresh();
    };

    const onLayoutChange = (event: Event) => {
      if (event.type === 'resize') resized = true;
      if (frame !== null) return;
      frame = window.requestAnimationFrame(sync);
    };

    document.addEventListener('scroll', onLayoutChange, { capture: true, passive: true });
    window.addEventListener('resize', onLayoutChange, { passive: true });

    this.stopTracking = () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      document.removeEventListener('scroll', onLayoutChange, { capture: true });
      window.removeEventListener('resize', onLayoutChange);
    };
  }

  private async adaptToViewport(instance: Driver): Promise<void> {
    const shape = sidebarShape();

    if (shape !== this.shape) {
      this.shape = shape;
      const active = this.run[instance.getActiveIndex() ?? -1];
      if (!active || anchorTargetsSidebar(active.step.anchor)) {
        this.mergeSidebarRestore(await ensureSidebarUsable());
      }
      if (this.instance !== instance) return;
      this.shape = sidebarShape();
      const moved = this.reresolveRun(instance.getActiveIndex() ?? -1);
      this.recountExpandable();
      if (moved) {
        this.readopt(instance);
        return;
      }
    }

    if (this.syncActiveAnchor()) {
      this.readopt(instance);
      return;
    }

    const entry = this.run[instance.getActiveIndex() ?? -1];
    if (!entry) {
      instance.refresh();
      return;
    }

    if (keepAnchorInView(entry.el)) {
      this.readopt(instance);
      return;
    }

    if (this.rescopeActive(instance)) {
      this.readopt(instance);
      return;
    }

    this.fitStep(instance.getActiveIndex() ?? -1, entry, liveGuide());
    instance.refresh();
    this.settleLayout(instance);
  }

  private fitStep(index: number, entry: RunEntry, card: HTMLElement | null): void {
    keepAnchorInView(entry.el);

    const placement = guideMode(entry.el.getBoundingClientRect());
    dressGuide(card, placement);

    const driveStep = this.driveSteps[index];
    if (driveStep?.popover) {
      driveStep.popover.side = placement.side ?? 'bottom';
    }

    const measured = card?.getBoundingClientRect().height ?? 0;
    makeRoomFor(entry.el, {
      height: measured > 0 ? measured : guideHeight(),
      sheet: placement.mode === 'sheet',
    });
  }

  private rescopeActive(instance: Driver): boolean {
    const index = instance.getActiveIndex() ?? -1;
    const entry = this.run[index];
    if (!entry || !this.expanded.has(entry.step.id)) return false;

    const labels =
      entry.scopeLabels ??
      this.moduleLabelsFor(this.childrenByParent.get(entry.step.id) ?? []);
    if (labels.length === 0) return false;

    const scope = accordionScopeFor(labels);
    if (!scope) return false;

    const fits = groupFits(scope.getBoundingClientRect().height, {
      height: guideHeight(),
      sheet: guideMode(scope.getBoundingClientRect()).mode === 'sheet',
    }, visibleBand(scope));

    if (fits && !entry.scopeLabels) {
      entry.scopeLabels = labels;
      this.retarget(index, scope);
      return true;
    }

    if (!fits && entry.scopeLabels) {
      const row = resolveAnchor(entry.step.anchor)?.element;
      if (!row) return false;
      entry.scopeLabels = undefined;
      this.retarget(index, row);
      return true;
    }

    return false;
  }

  private readopt(instance: Driver): void {
    const index = instance.getActiveIndex() ?? -1;
    if (index < 0 || !this.run[index]) {
      instance.refresh();
      return;
    }
    instance.moveTo(index);
    this.settleLayout(instance);
  }

  private reresolveRun(activeIndex: number): boolean {
    let activeMoved = false;
    for (let index = 0; index < this.run.length; index++) {
      const entry = this.run[index];
      if (entry.el.isConnected && isVisible(entry.el)) continue;
      const fresh = this.freshElementFor(entry);
      if (!fresh || fresh === entry.el) continue;
      this.retarget(index, fresh);
      if (index === activeIndex) activeMoved = true;
    }
    return activeMoved;
  }

  private recountExpandable(): void {
    const counts = new Map<string, number>();
    for (const { step } of this.run) {
      const children = this.childrenByParent.get(step.id);
      if (!children || children.length === 0) continue;
      const labels = this.moduleLabelsFor(children);
      if (labels.length === 0 || !accordionTriggerFor(labels)) continue;
      counts.set(step.id, children.length);
    }
    this.expandableCounts = counts;
  }

  private mergeSidebarRestore(next: SidebarRestore | null): void {
    if (!next) return;
    const held = this.sidebarRestore;
    this.sidebarRestore = held
      ? {
          openedDrawer: held.openedDrawer || next.openedDrawer,
          expandedRail: held.expandedRail || next.expandedRail,
        }
      : next;
  }

  private freshElementFor(entry: RunEntry): HTMLElement | null {
    return entry.scopeLabels
      ? accordionScopeFor(entry.scopeLabels)
      : resolveAnchor(entry.step.anchor)?.element ?? null;
  }

  private syncActiveAnchor(): boolean {
    const instance = this.instance;
    if (!instance) return false;

    const index = instance.getActiveIndex() ?? -1;
    const entry = this.run[index];
    if (!entry) return false;
    if (entry.el.isConnected && isVisible(entry.el)) return false;

    const fresh = this.freshElementFor(entry);
    if (!fresh || fresh === entry.el) return false;

    this.retarget(index, fresh);
    return true;
  }

  private retarget(index: number, element: HTMLElement): void {
    const entry = this.run[index];
    if (entry) entry.el = element;
    const driveStep = this.driveSteps[index];
    if (driveStep) driveStep.element = element;
  }

  private async afterPendingExpand(instance: Driver, fn: () => void): Promise<void> {
    await this.expandChain;
    if (this.instance !== instance) return;
    fn();
  }

  private async navigateTo(index: number): Promise<void> {
    const instance = this.instance;
    if (!instance) return;

    const entry = this.run[index];
    if (!entry) {
      instance.destroy();
      return;
    }

    await this.ensureContext(entry, index);
    if (this.instance !== instance) return;
    instance.moveTo(index);
    this.settleLayout(instance);
  }

  private dropStaleActiveMarks(current: Element | undefined): void {
    document.querySelectorAll('.driver-active-element').forEach((el) => {
      if (el === current) return;
      el.classList.remove('driver-active-element', 'driver-no-interaction');
    });
  }

  private async ensureNavFor(entry: RunEntry): Promise<void> {
    const wantsNav = anchorTargetsSidebar(entry.step.anchor);
    const shape = sidebarShape();

    if (wantsNav) {
      if (shape === 'absent') this.mergeSidebarRestore(await ensureSidebarUsable());
      return;
    }

    if (shape === 'drawer') await closeSidebarDrawer();
  }

  private async prepareGroups(entry: RunEntry): Promise<void> {
    const keep = this.keepOpenLabelsFor(entry);
    await collapseAccordionsExcept(keep);
    if (keep.length > 0) openAccordion(keep);
  }

  private keepOpenLabelsFor(entry: RunEntry): string[] {
    if (entry.step.requiresModule) return [entry.step.requiresModule];
    return this.moduleLabelsFor(this.childrenByParent.get(entry.step.id) ?? []);
  }

  private settleLayout(instance: Driver): void {
    if (this.settleFrame !== null) window.cancelAnimationFrame(this.settleFrame);

    const startedAt = Date.now();
    let last = '';
    let still = 0;

    const tick = () => {
      this.settleFrame = null;
      if (this.instance !== instance) return;

      const index = instance.getActiveIndex() ?? -1;
      const entry = this.run[index];
      if (!entry) return;

      keepTrackerClear();

      const rect = entry.el.getBoundingClientRect();
      const box = `${Math.round(rect.top)}:${Math.round(rect.height)}`;

      if (box === last) {
        still += 1;
      } else {
        still = 0;
        last = box;
        this.fitStep(index, entry, liveGuide());
        instance.refresh();
      }

      if (still >= SETTLE_STILL_FRAMES || Date.now() - startedAt >= SETTLE_MS) return;
      this.settleFrame = window.requestAnimationFrame(tick);
    };

    this.settleFrame = window.requestAnimationFrame(tick);
  }

  private async ensureContext(entry: RunEntry, index: number): Promise<void> {
    await this.ensureNavFor(entry);
    await this.prepareGroups(entry);

    if (entry.el.isConnected && isVisible(entry.el)) return;

    const moduleLabel = entry.step.requiresModule;
    if (moduleLabel) {
      openAccordion([moduleLabel]);
      const fresh = await waitForDom(
        () => resolveAnchor(entry.step.anchor),
        SUBMENU_MOUNT_TIMEOUT_MS,
      );
      if (fresh) this.retarget(index, fresh.element);
      return;
    }

    const fresh = this.freshElementFor(entry);
    if (fresh) this.retarget(index, fresh);
  }

  private driveStep({ step, el }: RunEntry): DriveStep {
    return {
      element: el,
      disableActiveInteraction: step.lockTarget ? true : undefined,
      popover: {
        title: step.title,
        description: this.describeStep(step),
        side: step.side ?? 'right',
        align: 'start',
      },
    };
  }

  private describeStep(step: TourStep): string {
    const children = this.childrenByParent.get(step.id);
    if (!children || children.length === 0) return step.description;
    const labels = children.map((child) => child.navLabel ?? child.title);
    return `${step.description} Includes: ${labels.join(', ')}.`;
  }

  private buildTaskButton(step: TourStep): HTMLButtonElement | null {
    const offer = this.options.taskOffer?.(step.id);
    if (!offer) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = TASK_CLASS;
    button.setAttribute('data-testid', 'tour-start-task');

    const label = document.createElement('span');
    label.textContent = offer.label;
    const arrow = document.createElement('span');
    arrow.className = `${TASK_CLASS}__arrow`;
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '↗';
    button.append(label, arrow);

    button.addEventListener('click', () => this.options.onTaskStart?.(step.id));

    return button;
  }

  private buildSkipModuleButton(at: number): HTMLButtonElement | null {
    const nextModule = this.nextModuleIndex(this.moduleIndexFor(at));
    if (nextModule >= this.run.length || nextModule <= at + 1) return null;

    const label = this.run[nextModule]?.step.navLabel ?? null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = SKIP_CLASS;
    button.setAttribute('data-testid', 'tour-skip-module');
    button.textContent = label ? `Skip to ${label}` : 'Skip this module';
    button.addEventListener('click', () => this.nextModule());

    return button;
  }

  private buildSkipTourButton(at: number): HTMLButtonElement | null {
    if (at >= this.run.length - 1) return null;

    const button = document.createElement('button');
    button.type = 'button';
    button.className = SKIP_TOUR_CLASS;
    button.setAttribute('data-testid', 'tour-skip');
    button.textContent = 'Skip';
    button.addEventListener('click', () => this.stop());

    return button;
  }

  private keepModalsOpen(wrapper: HTMLElement): void {
    const swallow = (event: Event) => event.stopPropagation();
    wrapper.addEventListener('pointerdown', swallow);
    wrapper.addEventListener('mousedown', swallow);
  }

  private moduleLabelsFor(children: TourStep[]): string[] {
    const labels: string[] = [];
    for (const child of children) {
      const label = child.requiresModule;
      if (label && !labels.includes(label)) labels.push(label);
    }
    return labels;
  }

  private expandChildren(parentStepId: string): Promise<void> {
    const run = async (): Promise<void> => {
      const instance = this.instance;
      if (!instance || this.expanded.has(parentStepId)) return;

      const children = this.childrenByParent.get(parentStepId);
      const at = this.runOrder.indexOf(parentStepId);
      if (!children || children.length === 0 || at < 0) return;

      const standingOn = this.run[instance.getActiveIndex() ?? -1]?.step.id ?? null;

      const moduleLabels = this.moduleLabelsFor(children);
      openAccordion(moduleLabels);
      const resolvedEntries = await this.resolveChildren(children);

      this.expanded.add(parentStepId);

      const scope = accordionScopeFor(moduleLabels);
      const framed =
        scope &&
        groupFits(scope.getBoundingClientRect().height, {
          height: guideHeight(),
          sheet: guideMode(scope.getBoundingClientRect()).mode === 'sheet',
        }, visibleBand(scope));
      if (scope && framed) {
        const parentEntry = this.run[at];
        if (parentEntry) parentEntry.scopeLabels = moduleLabels;
        this.retarget(at, scope);
      }

      const entries = resolvedEntries.filter((entry) => this.options.taskOffer?.(entry.step.id));

      if (entries.length === 0) {
        this.reanchor(instance, standingOn, at);
        return;
      }

      this.run.splice(at + 1, 0, ...entries);
      this.driveSteps.splice(at + 1, 0, ...entries.map((entry) => this.driveStep(entry)));
      this.runOrder = this.run.map((r) => r.step.id);
      this.reanchor(instance, standingOn, at);
    };

    const chained = this.expandChain.then(run, run);
    this.expandChain = chained.catch(() => {});
    return chained;
  }

  private reanchor(instance: Driver, stepId: string | null, fallbackIndex: number): void {
    const index = stepId !== null ? this.runOrder.indexOf(stepId) : -1;
    instance.moveTo(index >= 0 ? index : fallbackIndex);
    this.settleLayout(instance);
  }

  private async resolveChildren(children: TourStep[]): Promise<RunEntry[]> {
    const attempt = (): RunEntry[] => {
      const entries: RunEntry[] = [];
      for (const step of children) {
        const hit = resolveAnchor(step.anchor);
        if (hit) entries.push({ step, el: hit.element, strategy: hit.strategy });
      }
      return entries;
    };

    const complete = await waitForDom(() => {
      const entries = attempt();
      return entries.length === children.length ? entries : null;
    }, SUBMENU_MOUNT_TIMEOUT_MS);

    return complete ?? attempt();
  }
}
